import TopicsContainer from '@/components/topics/TopicsContainer';
import CoursePath from '@/models/CoursePath';
import { TempLesson } from '@/models/Lesson';
import { useLessonsBlockWithLessons } from '@/pages/admin/courses/studio/CourseStudio.hooks';
import { StudioStep } from '@/pages/admin/courses/studio/info/CourseStudioInfo.hooks';
import CourseStudioMatrix from '@/pages/admin/courses/studio/matrix/CourseStudioMatrix';
import CourseStudioObjectives from '@/pages/admin/courses/studio/objectives/CourseStudioObjectives';
import { ROUTES } from '@/utils/routes';
import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import useLessonBox from './lesson/LessonBox.hooks';
import { CourseBoxMainSkeleton } from './skeletons/CourseBoxSkeletons';
import useLessons from '@/pages/admin/ManageCourseEditing.hook';

export type CourseBoxMainProps = {
  coursePath: CoursePath;
};

export default function CourseBoxMain({ coursePath }: CourseBoxMainProps) {
  const navigate = useNavigate();

  const { lessonBlocksWithLessons, isLoading } = useLessonsBlockWithLessons(
    coursePath.slug,
  );

  const { directLessons: lessonsOnly } = useLessons(coursePath.slug);

  const [isEditing, setIsEditing] = useState(false);

  const onClick = ({ lesson }: { lesson: TempLesson }) => {
    navigate(`/courses/box/${coursePath.slug}/lesson/${lesson.order}`);
  };

  const { lesson } = useLessonBox();

  const goToStudio = (step: StudioStep) => {
    navigate(ROUTES.COURSES.STUDIO.STEP(coursePath.slug, step));
  };

  if (isLoading) {
    return <CourseBoxMainSkeleton />;
  }

  return (
    <main className="relative flex gap-4 w-full h-full overflow-hidden">
      <TopicsContainer className="relative w-1/5 min-w-[250px] max-w-[500px] h-full flex flex-col gap-2.5 border-neutral-content overflow-hidden">
        <div className="flex flex-col gap-2.5 flex-1 min-h-0">
          <CourseStudioObjectives
            objectives={coursePath.objectives}
            onEdit={() => goToStudio('objectives')}
            className="flex-1 min-h-0"
            render
          />
          <CourseStudioMatrix
            blocks={lessonBlocksWithLessons}
            onClick={onClick}
            directLessons={lessonsOnly}
            selectedLesson={lesson}
            onEdit={() => goToStudio('matrix')}
            hasOpen={true}
            disabled={isEditing}
            className="flex-1 min-h-0"
          />
        </div>
      </TopicsContainer>
      <Outlet context={{ isEditing, setIsEditing }} />
    </main>
  );
}
